import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import InBodyKickUserDialog from "@icoach/inbody/dialog/InBodyKickUserDialog";
import InBodyImpedanceWarningDialog from "@icoach/inbody/dialog/InBodyImpedanceWarningDialog";
import useInBodyApi from "@apis/useInBodyApi";
import { isEmpty, isEqual, isFunction, isNumber } from "@util/utils";
import { MEASURE_REFERENCE_TYPE } from "@icoach/constants/";
import InBodyMeasurementInputDialog from "@icoach/inbody/dialog/InBodyMeasurementInputDialog";

const INBODYMEASUREMENTINPUT = "inBodyMeasurementInput"; // 量身表單
const INBODYKICKUSER = "inBodyKickUser"; // 按下量身前檢查是否有人在裝置上畫面
const INBODYIMPEDANCEWARNING = "inBodyImpedanceWarning"; // 量完身後異常阻抗畫面

/** ================================
 * 📌 量身使用
 * 量身流程所需底下Dialog不可拆成單獨
 * @typedef {Object} SourceDataProps
 * @property {boolean} [isInterviewNotMeasure=false] - 是否只面談不量身
 * @property {string} [referenceType] - 來源類型
 * @property {number} [peopleID=0] - 對象ID
 * @property {string} [nickName=''] - 暱稱
 * @property {boolean} [isMenHidden=false] - 是否隱藏男性選項
 * @property {number} [employeeID] - 教練ID
 * @property {string} [gender] - 性別 1=女, 2=男
 *  ================================
 */
const InBodyStartScanDialogs = React.forwardRef((props, ref) => {
	const { open, onClose, sourceData: sourceDataProps } = props;
	if (!open) return null;
	/**
	 * @type {SourceDataProps}
	 */
	const { isInterviewNotMeasure = false, referenceType, peopleID = 0, nickName = "", isMenHidden = false, employeeID, gender: genderProps = 1 } = sourceDataProps || {};
	const { enqueueSnackbar } = useSnackbar();
	const { getMemberHeightApi, getEmployeeHeightApi } = useInBodyApi();
	const [height, setHeight] = useState(null);
	const [gender, setGender] = useState(genderProps);
	const [openDialog, setOpenDialog] = useState(null);
	const handleCloseDialog = () => {
		isFunction(onClose) && onClose();
	};

	const triggerMeasurementDialog = params => {
		if (isInterviewNotMeasure) {
			enqueueSnackbar("已勾選只面談不量身，無法操作量身", { variant: "info" });
			handleCloseDialog();
		} else {
			if (isEqual(referenceType, MEASURE_REFERENCE_TYPE.EMPLOYEE)) {
				getEmployeeHeight();
			} else if (isEqual(referenceType, MEASURE_REFERENCE_TYPE.MEMBER)) {
				getMemberHeight();
			} else {
				setHeight(0);
				setOpenDialog(INBODYMEASUREMENTINPUT);
			}
		}
	};

	const handleToggleDialog = target => {
		setOpenDialog(target);
	};

	const getMemberHeight = async () => {
		const resp = await getMemberHeightApi(peopleID);
		if (isNumber(resp)) {
			setHeight(resp);
			setOpenDialog(INBODYMEASUREMENTINPUT);
		}
	};

	const getEmployeeHeight = async () => {
		const resp = await getEmployeeHeightApi(peopleID);
		if (!isEmpty(resp)) {
			setHeight(resp.height);
			setGender(resp.gender);
			setOpenDialog(INBODYMEASUREMENTINPUT);
		}
	};

	useEffect(
		() => {
			triggerMeasurementDialog();
			return () => handleCloseDialog();
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<InBodyMeasurementInputDialog
				key={height}
				open={isEqual(INBODYMEASUREMENTINPUT, openDialog)}
				onClose={handleCloseDialog}
				sourceData={{
					onInBodyKickUserDialog: () => handleToggleDialog(INBODYKICKUSER),
					onInBodyImpedanceWarningDialog: () => handleToggleDialog(INBODYIMPEDANCEWARNING),
					height: height,
					referenceType,
					peopleID: peopleID,
					nickName: nickName,
					isMenHidden: isMenHidden,
					employeeID: employeeID,
					gender: gender,
				}}
			/>
			<InBodyKickUserDialog open={isEqual(INBODYKICKUSER, openDialog)} onClose={handleCloseDialog} sourceData={{ onRedoMeasurement: triggerMeasurementDialog }} />
			<InBodyImpedanceWarningDialog open={isEqual(INBODYIMPEDANCEWARNING, openDialog)} onClose={handleCloseDialog} sourceData={{ onRedoMeasurement: triggerMeasurementDialog }} />
		</React.Fragment>
	);
});

export default InBodyStartScanDialogs;
